import {Component, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute, Router, RouterLink} from '@angular/router';
import {Subject, takeUntil} from 'rxjs';
import {NgForOf} from '@angular/common';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import moment from 'moment';
import {FilterService} from '../../../../services/filter.service';

@Component({
  selector: 'app-room-list-filter',
  imports: [
    RouterLink,
    NgForOf,
    ReactiveFormsModule,
    FormsModule
  ],
  templateUrl: './room-list-filter.component.html',
  styleUrl: './room-list-filter.component.scss'
})
export class RoomListFilterComponent implements OnInit, OnDestroy {
  onDestroy$ = new Subject<boolean>();

  isCoworkingSelected = false
  isConferenceRoomSelected = false

  regionSlug: string | null = null
  neighborhoodSlug: string | null = null

  coworkingPath: string = "/discover"
  conferenceRoomPath: string = "/conference-room"

  // Reserve options
  reserveDate: string | null = null
  reserveTime: string | null = null
  reserveDuration: number | null = null
  reservePeople: number | null = null

  reserveDateOptions: { label: string, value: string }[] = []
  reserveTimeOptions: { label: string, value: string }[] = []
  reserveDurationOptions: { label: string, value: number }[] = []
  reservePeopleOptions: { label: string, value: number }[] = []

  constructor(private router: Router,
              private route: ActivatedRoute,
              private filterService: FilterService) {
  }

  ngOnInit() {
    this.route.params.pipe(takeUntil(this.onDestroy$)).subscribe(params => {
      this.regionSlug = params['regionSlug']
      this.neighborhoodSlug = params['neighborhoodSlug']

      this.isCoworkingSelected = this.router.url.startsWith("/discover")
      this.isConferenceRoomSelected = this.router.url.startsWith("/conference-room")

      this.refreshLinks()
      this.refreshReservation()
    })

    this.filterService.reset$.pipe(takeUntil(this.onDestroy$)).subscribe(reset => {
      if (reset) {
        setTimeout(() => {
          this.reserveDate = null
          this.reserveTime = null
          this.reserveDuration = null
          this.reservePeople = null

          this.reserveDateOptions = []
          this.reserveTimeOptions = []
          this.reserveDurationOptions = []
          this.reservePeopleOptions = []

          this.refreshReservation()
        }, 0);
      }
    });
  }

  ngOnDestroy() {
    this.onDestroy$.next(true);
    this.onDestroy$.complete();
  }

  refreshLinks() {
    if (this.regionSlug && this.neighborhoodSlug) {
      this.coworkingPath = `/discover/${this.regionSlug}/${this.neighborhoodSlug}`
      this.conferenceRoomPath = `/conference-room/${this.regionSlug}/${this.neighborhoodSlug}`
      return
    }

    if (this.regionSlug) {
      this.coworkingPath = `/discover/${this.regionSlug}`
      this.conferenceRoomPath = `/conference-room/${this.regionSlug}`
      return
    }

    this.coworkingPath = `/discover`
    this.conferenceRoomPath = `/conference-room`
  }

  refreshReservation() {

    // Reserve date options [2025-01-01 -> 2025-03-01]
    let currentDay = moment();
    for (let i = 0; i < 60; i++) {
      this.reserveDateOptions.push({
        value: currentDay.format('yyyy-MM-DD'),
        label: currentDay.format('MMM Do, YYYY')
      });
      currentDay.add(1, "day")
    }

    // Reserve time options [07:00 -> 20:00]
    let currentTime = moment().hour(7).minute(0).second(0);
    for (let i = 0; i < 27; i++) {
      this.reserveTimeOptions.push({
        value: currentTime.format('hh:mm:ss'),
        label: currentTime.format('h:mm a')
      });
      currentTime = currentTime.add(30, 'minutes');
    }

    // Reserve duration options [0.5 -> 8.0]
    for (let i = 0.5; i <= 8; i += 0.5) {
      this.reserveDurationOptions.push({
        value: i,
        label: i === 0.5 ? '30 mins' : Math.floor(i) === i ? i + ' hour' + (i !== 1 ? 's' : '') : Math.floor(i) + ' hours & 30 mins',
      });
    }

    // Reserve people options [1 -> 8]
    for (let i = 1; i <= 8; i += 1) {
      this.reservePeopleOptions.push({
        value: i,
        label: i === 1 ? `${i} person` : `${i} people`,
      });
    }

    let skipDays = 1 // Always tomorrow
    if (moment().weekday() == 5) skipDays += 2 // Friday   -> Monday
    if (moment().weekday() == 6) skipDays += 1 // Saturday -> Monday
    if (moment().weekday() == 7) skipDays += 0 // Sunday   -> Monday

    // Query defaults
    const queryParamsReserve = this.route.snapshot.queryParams['reserve']
    const queryParamsReserveDuration = this.route.snapshot.queryParams['reserveDuration']
    const queryParamsReservePeople = this.route.snapshot.queryParams['reservePeople']

    if (queryParamsReserve) this.reserveDate = moment(queryParamsReserve).format('yyyy-MM-DD')
    if (queryParamsReserve) this.reserveTime = moment(queryParamsReserve).format('hh:mm:ss')
    if (queryParamsReserveDuration) this.reserveDuration = queryParamsReserveDuration
    if (queryParamsReservePeople) this.reservePeople = queryParamsReservePeople

    // Defaults
    if (!this.reserveDate) this.reserveDate = this.reserveDateOptions[skipDays].value      // Monday
    if (!this.reserveTime) this.reserveTime = this.reserveTimeOptions[4].value             // 9:00 am
    if (!this.reserveDuration) this.reserveDuration = this.reserveDurationOptions[1].value // 1 hour
    if (!this.reservePeople) this.reservePeople = this.reservePeopleOptions[1].value       // 2 people
  }

  changeReserveFilters() {
    this.filterService.addFilters({
      reserve: `${this.reserveDate}T${this.reserveTime}`,
      reserveDuration: this.reserveDuration,
      reservePeople: this.reservePeople,
    });
  }
}
